import { useLazyQuery, useMutation } from '@apollo/client';
import { faPlay } from '@fortawesome/pro-regular-svg-icons';
import { highlight, languages } from 'prismjs/components/prism-core';
import { useMemo, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-csv';
import 'prismjs/themes/prism.css';
import Editor from 'react-simple-code-editor';

import { getOutputType, getTxtDelimiter } from './OutputSettings.tsx';
import type { OutputMapperTestingConfig, RecordType, TestRecord, TestResult } from './types.ts';
import Button from '../../../core/components/Button.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import type { OutputMapperConfig } from '../OutputMapperEditor/types.ts';

const PlayIcon = withIcon(faPlay);

const CodeEditorPanel = ({ title, code, grammar }: { title: string; code: string; grammar: any }) => (
    <div className="flex flex-col h-full border border-line-weak rounded-md overflow-hidden">
        <div className="px-3 py-2 bg-surface-medium text-sm font-medium border-b border-line-weak flex-none">
            {title}
        </div>
        <div className="flex-1 min-h-0 bg-surface-weak overflow-auto">
            <Editor
                className="text-xs font-mono"
                value={code}
                onValueChange={() => {}}
                highlight={(input) => highlight(input, grammar)}
                disabled
                padding={12}
            />
        </div>
    </div>
);

const JsonPanel = ({ title, data }: { title: string; data: any }) => {
    const formattedJson = useMemo(() => {
        if (!data) return '';
        if (typeof data === 'string') {
            try {
                return JSON.stringify(JSON.parse(data), null, 2);
            } catch {
                return data;
            }
        }
        return JSON.stringify(data, null, 2);
    }, [data]);

    if (!data) return null;

    return <CodeEditorPanel title={title} code={formattedJson} grammar={languages.json} />;
};

const XmlPanel = ({ title, xml }: { title: string; xml: string }) => (
    <CodeEditorPanel title={title} code={xml} grammar={languages.markup} />
);

const CsvPanel = ({ title, csv }: { title: string; csv: string }) => (
    <CodeEditorPanel title={title} code={csv} grammar={languages.csv} />
);

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Mirrors prism-csv's grammar (see prismjs/components/prism-csv.js) but honors the configured delimiter. */
const buildTxtGrammar = (delimiter: string) => {
    const escapedDelimiter = escapeRegExp(delimiter || ',');
    return {
        value: new RegExp(`[^\\r\\n${escapedDelimiter}"]+|"(?:[^"]|"")*"(?!")`),
        punctuation: new RegExp(escapedDelimiter),
    };
};

const TxtPanel = ({ title, txt, delimiter }: { title: string; txt: string; delimiter: string }) => (
    <CodeEditorPanel title={title} code={txt} grammar={buildTxtGrammar(delimiter)} />
);

interface MappingTestControlsProps {
    integrationId?: string;
    recordType: RecordType;
    config: OutputMapperConfig | null;
    testingConfig: OutputMapperTestingConfig;
    renderMappedOutput?: (mappedOutput: any, config: OutputMapperConfig | null) => React.ReactNode;
    /** Receives every test result, e.g. for an inline preview. */
    onResult?: (result: TestResult) => void;
    /** Open the input/output dialog after a run (default); off for inline previews. */
    showResultModal?: boolean;
    runLabel?: string;
}

/**
 * Compact "Test record" selector + "Run test" button rendered inside the visual editor toolbar.
 * Running a mapping opens a modal with the input data and the produced output side by side — replacing
 * the former standalone Mapping-tests tab.
 */
const MappingTestControls = ({
    integrationId,
    recordType,
    config,
    testingConfig,
    renderMappedOutput,
    onResult,
    showResultModal = true,
    runLabel,
}: MappingTestControlsProps) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const [selectedRecordId, setSelectedRecordId] = useState<string>('');
    const [testResult, setTestResult] = useState<TestResult | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    const [fetchTestRecords, { data: testRecordsData, loading: loadingRecords }] = useLazyQuery(
        testingConfig.fetchTestRecordsQuery,
        { fetchPolicy: 'network-only' }
    );

    const [testMapping, { loading: testingMapping }] = useMutation(testingConfig.testMappingMutation);

    const buildFetchVariables =
        testingConfig.buildFetchVariables ?? ((id: string, limit: number) => ({ integrationId: id, limit }));
    const buildTestVariables =
        testingConfig.buildTestVariables ??
        ((recordId: string, type: RecordType, liveConfig: OutputMapperConfig | null) => ({
            recordId,
            recordType: type,
            config: liveConfig,
            returnType: liveConfig?.output_type ?? 'json',
        }));

    React.useEffect(() => {
        if (integrationId) {
            fetchTestRecords({ variables: buildFetchVariables(integrationId, 10) });
        }
    }, [integrationId, fetchTestRecords, buildFetchVariables]);

    const records: TestRecord[] = testRecordsData ? testingConfig.getRecordsFromData(testRecordsData, recordType) : [];

    React.useEffect(() => {
        if (!selectedRecordId && records.length > 0) {
            setSelectedRecordId(records[0].id);
        }
    }, [records, selectedRecordId]);

    const recordOptions = records.map((record) => ({
        value: record.id,
        label: `${record.orderNumber || record.requestNumber || record.invoiceNumber || record.documentNumber || record.subject || 'Record'} (${record.channelName})`,
        description: new Date(record.createdAt).toLocaleString(),
    }));

    const handleRunTest = async () => {
        if (!integrationId || !selectedRecordId || !config) return;
        try {
            const result = await testMapping({ variables: buildTestVariables(selectedRecordId, recordType, config) });
            const resultData = testingConfig.getTestResultFromData(result.data);
            setTestResult(resultData);
            onResult?.(resultData);
            if (showResultModal) setModalOpen(true);
            if (resultData?.success) {
                publishToast({ description: t('outputMapper.form.testSuccess'), status: 'success' });
            } else {
                publishToast({ description: t('outputMapper.form.testFailed'), status: 'error' });
            }
        } catch (error) {
            console.error(error);
            publishToast({ description: t('outputMapper.form.testFailed'), status: 'error' });
        }
    };

    const canRun = Boolean(integrationId && selectedRecordId && config) && !testingMapping;
    const outputType = getOutputType(config);

    return (
        <div className="flex items-center gap-2 flex-none">
            <span className="text-xs font-medium text-content-weak whitespace-nowrap">
                {t('outputMapper.form.testRecord', 'Test record')}
            </span>
            <div className="w-[21rem]">
                <SelectField
                    options={recordOptions}
                    value={selectedRecordId}
                    onValueChange={setSelectedRecordId}
                    placeholder={t('outputMapper.form.selectRecordPlaceholder')}
                    isLoading={loadingRecords}
                    renderValue={(value, option) => <span className="block truncate">{option?.label ?? value}</span>}
                />
            </div>
            <Button type="button" variant="outline" onClick={handleRunTest} disabled={!canRun}>
                <PlayIcon className="mr-2" />
                {testingMapping
                    ? t('outputMapper.form.testing')
                    : runLabel || t('outputMapper.form.testMapping', 'Run test')}
            </Button>

            {modalOpen && testResult && (
                <Modal
                    visible
                    title={
                        <span className="flex items-center gap-2">
                            <span
                                className={
                                    testResult.success ? 'text-status-success-medium' : 'text-status-danger-medium'
                                }
                            >
                                {testResult.success
                                    ? t('outputMapper.form.testSuccess')
                                    : t('outputMapper.form.testFailed')}
                            </span>
                            {testResult.configName && (
                                <span className="text-sm font-normal text-content-weak">{testResult.configName}</span>
                            )}
                            {testResult.fileName && (
                                <span className="font-mono text-sm font-normal text-content-medium">
                                    {t('outputMapper.form.fileName', {
                                        defaultValue: 'File: {{name}}',
                                        name: testResult.fileName,
                                    })}
                                </span>
                            )}
                        </span>
                    }
                    className="w-[110rem] !max-w-[92vw]"
                    onClose={() => setModalOpen(false)}
                >
                    <div className="flex flex-col gap-3 h-[72vh] min-h-0">
                        {testResult.error && (
                            <div className="text-sm text-status-danger-medium bg-status-danger-weak p-2 rounded flex-none">
                                {testResult.error}
                            </div>
                        )}
                        <div className="flex-1 min-h-0 grid grid-cols-2 gap-4">
                            <JsonPanel title={t('outputMapper.form.inputData')} data={testResult.inputData} />
                            {/* Gate each panel on the configured output_type first — a non-empty mappedOutputXxx
                                field alone isn't proof that format is the intended one (e.g. a stale value left
                                over from a resolver that didn't null out the other format fields). */}
                            {outputType === 'xml' &&
                            typeof testResult.mappedOutputXml === 'string' &&
                            testResult.mappedOutputXml !== '' ? (
                                <XmlPanel
                                    title={t('outputMapper.form.mappedOutput')}
                                    xml={testResult.mappedOutputXml}
                                />
                            ) : outputType === 'template' &&
                              typeof testResult.mappedOutputTemplate === 'string' &&
                              testResult.mappedOutputTemplate !== '' ? (
                                <XmlPanel
                                    title={t('outputMapper.form.mappedOutput')}
                                    xml={testResult.mappedOutputTemplate}
                                />
                            ) : outputType === 'csv' &&
                              typeof testResult.mappedOutputCsv === 'string' &&
                              testResult.mappedOutputCsv !== '' ? (
                                <CsvPanel
                                    title={t('outputMapper.form.mappedOutput')}
                                    csv={testResult.mappedOutputCsv}
                                />
                            ) : outputType === 'txt' &&
                              typeof testResult.mappedOutputTxt === 'string' &&
                              testResult.mappedOutputTxt !== '' ? (
                                <TxtPanel
                                    title={t('outputMapper.form.mappedOutput')}
                                    txt={testResult.mappedOutputTxt}
                                    delimiter={getTxtDelimiter(config)}
                                />
                            ) : (
                                (renderMappedOutput?.(testResult.mappedOutput, config) ?? (
                                    <JsonPanel
                                        title={t('outputMapper.form.mappedOutput')}
                                        data={testResult.mappedOutput}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default MappingTestControls;
